import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { useLang } from "@/i18n";
import Reveal from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function GoogleG({ className = "w-6 h-6" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.87-3c-1.07.72-2.44 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24z" />
      <path fill="#FBBC05" d="M5.27 14.28A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.28v-3.1H1.29a12 12 0 0 0 0 10.76l3.98-3.1z" />
      <path fill="#EA4335" d="M12 4.76c1.76 0 3.34.61 4.59 1.8l3.44-3.44A11.97 11.97 0 0 0 12 0 12 12 0 0 0 1.29 6.62l3.98 3.1C6.22 6.87 8.87 4.76 12 4.76z" />
    </svg>
  );
}

const Stars = ({ value = 5, size = "w-4 h-4" }) => (
  <div className="flex gap-1" aria-label={`${value} stars`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={size} fill={i < Math.round(value) ? "#fbbf24" : "none"} stroke="#fbbf24" />
    ))}
  </div>
);

export default function Reviews() {
  const { t } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API}/reviews`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && d.enabled) setData(d);
      })
      .catch(() => {});
  }, []);

  if (!data) return null;

  return (
    <section id="reviews" data-testid="google-reviews-section" className="relative border-t border-white/5 bg-[#050a16]/50 py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
          <div className="max-w-2xl">
            <Reveal>
              <span className="font-mono-accent text-xs uppercase tracking-[0.25em] text-cyan-400">{t.reviews.badge}</span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 data-testid="reviews-title" className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1]">
                {t.reviews.title}
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <span data-testid="google-rating-value" className="font-display text-4xl font-extrabold text-white">
                  {data.rating != null ? data.rating.toFixed(1) : "—"}
                </span>
                <Stars value={data.rating || 5} />
                <span className="text-sm text-slate-400">
                  {data.count} {t.reviews.countLabel}
                </span>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <div data-testid="google-live-badge" className="inline-flex items-center gap-3 rounded-2xl border border-white/15 bg-white/[0.04] px-5 min-h-14">
              <GoogleG />
              <span className="text-sm font-semibold text-slate-200">{t.reviews.liveBadge}</span>
            </div>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {data.reviews.map((r, i) => (
            <Reveal key={`${r.author}-${i}`} delay={i * 0.12}>
              <figure
                data-testid={`google-review-card-${i + 1}`}
                className="flex h-full flex-col rounded-3xl border border-white/10 bg-[#0b0f19] p-7 transition-colors duration-500 hover:border-cyan-400/30"
              >
                <div className="flex items-center justify-between">
                  <Stars value={r.rating} />
                  <GoogleG className="w-5 h-5 opacity-80" />
                </div>
                <blockquote className="mt-5 flex-1 text-sm sm:text-base text-slate-300 leading-relaxed">
                  “{r.text}”
                </blockquote>
                <figcaption className="mt-6 flex items-center gap-4 border-t border-white/5 pt-5">
                  <span className="vortex-btn flex h-11 w-11 items-center justify-center rounded-full font-display text-sm font-bold text-white">
                    {r.author.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-slate-100">{r.author}</span>
                    <span className="block text-xs text-slate-500 mt-0.5">{r.when}</span>
                  </span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
